<template>
  <div class="flyout-cart" :class="{ 'is-open': isOpen }">
    <header>
      <h3 class="flyout-cart-title">{{ title }}</h3>
      <span class="flyout-cart-subtitle">{{ subtitle }}</span>
      <button
        class="flyout-cart-close"
        :aria-label="closeButtonLabel"
        :tabindex="isOpen === false ? -1 : 0"
        @click="isOpen = false"
      >
        <svg class="icon icon-close">
          <use xlink:href="#icon-close" />
        </svg>
      </button>
    </header>
    <div ref="scrollTarget" class="line-items">
      <header v-if="!isEmpty">
        <span class="line-items-header-item">{{ productLabel }}</span>
        <span class="line-items-header-item">{{ quantityLabel }}</span>
      </header>
      <component
        :is="item.component"
        v-for="item in items"
        :key="'' + item.id + item.price"
        :compare-price="item.compareAtPrice | money(moneyFormat)"
        :image-alt="item.image.alt"
        :image-src="item.image.src"
        :is-focusable="isOpen"
        :multiplier="item.multiplier"
        :properties="item.properties"
        :quantity="item.quantity"
        :remove-label="removeLabel"
        :subscription-label="subscriptionLabel"
        :subtitle="item.subtitle"
        :title="item.title"
        :price="item.price | money(moneyFormat)"
        :url="item.url"
        :variant-id="item.id"
        :flash-message="item.flashMessage"
        :selling-plan-allocation="item.sellingPlanAllocation"
        @update="updateItem({ item, quantity: $event })"
        @remove="removeItem({ item })"
      />
    </div>
    <footer>
      <span v-if="subTotal > 0" class="flyout-cart-subtotal">
        {{ subTotal | money(moneyFormat) }}
      </span>
      <span v-if="subTotal > 0" class="flyout-cart-footer-text">
        {{ taxesAndShippingLabel }}
      </span>
      <button
        ref="checkout"
        :tabindex="isOpen === false ? -1 : 0"
        :aria-label="checkoutButtonLabel"
        class="button-segmented button-segmented-grey"
        type="button"
        @click="onCheckoutButtonClick"
      >
        <span>{{ checkoutButtonLabel }}</span>
        <svg class="icon icon-arrow-right">
          <use xlink:href="#icon-arrow-right" />
        </svg>
      </button>
    </footer>
    <div class="flyout-cart-overlay" @click="isOpen = !isOpen" />
  </div>
</template>

<script>
import * as ShopifyCurrency from '@shopify/theme-currency';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks
} from 'body-scroll-lock';

import LineItem from './LineItem.vue';
import FreeGiftLineItem from './FreeGiftLineItem.vue';

import { createRechargeCheckoutURL } from '../../helper/recharge';

let previouslyFocusedElement = undefined;

import { mapState, mapActions, mapGetters } from 'vuex';
import { flyoutCart, actions } from './store';

export default {
  filters: {
    money(value, moneyFormat) {
      return value && ShopifyCurrency.formatMoney(value, moneyFormat);
    }
  },

  components: {
    FreeGiftLineItem,
    LineItem
  },

  props: {
    closeButtonLabel: {
      default: '',
      type: String
    },
    customerEmail: {
      default: '',
      type: String
    },
    customerId: {
      default: 0,
      type: Number
    },
    emptyCheckoutButtonLabel: {
      default: '',
      type: String
    },
    emptySubtitle: {
      default: '',
      type: String
    },
    fullCheckoutButtonLabel: {
      default: '',
      type: String
    },
    fullSubtitle: {
      default: '',
      type: String
    },
    loginUrl: {
      default: '',
      type: String
    },
    moneyFormat: {
      default: '${{amount}}',
      type: String
    },
    notEnoughStockText: {
      default: '',
      type: String
    },
    productLabel: {
      default: '',
      type: String
    },
    quantityLabel: {
      default: '',
      type: String
    },
    removeLabel: {
      default: '',
      type: String
    },
    requireCustomerForCheckout: {
      default: true,
      type: Boolean
    },
    rootUrl: {
      default: '',
      type: String
    },
    shopDomain: {
      default: '',
      type: String
    },
    subscriptionLabel: {
      default: '',
      type: String
    },
    taxesAndShippingLabel: {
      default: '',
      type: String
    },
    title: {
      default: '',
      type: String
    },
    updatingCheckoutButtonLabel: {
      default: '',
      type: String
    }
  },

  data() {
    return {
      isOpen: false
    };
  },

  computed: {
    ...mapState(flyoutCart, ['token', 'items', 'itemCount', 'subTotal']),

    ...mapGetters(flyoutCart, [
      'machines',
      'discountedMachines',
      'hasMachines',
      'subscriptionItems',
      'capsuleSubscriptionItems',
      'hasSubscriptionItems',
      'hasMinSubscriptionOrder',
      'isEmpty',
      'freeGift'
    ]),

    subtitle() {
      return this.isEmpty ? this.emptySubtitle : this.fullSubtitle;
    },

    checkoutButtonLabel() {
      if (this.isUpdating) {
        return this.updatingCheckoutButtonLabel;
      }
      if (this.isEmpty) {
        return this.emptyCheckoutButtonLabel;
      } else {
        return this.fullCheckoutButtonLabel;
      }
    },

    checkoutURL() {
      let checkoutUrl = `${this.rootUrl}checkout`;

      // Unless Shopify Subscriptions is enabled, we need to redirect to
      // ReCharge's custom checkout.
      if (this.hasSubscriptionItems && !__SHOPIFY_SUBSCRIPTIONS__) {
        checkoutUrl = createRechargeCheckoutURL(
          this.customerId,
          this.customerEmail,
          this.token,
          this.shopDomain
        );
      }

      return checkoutUrl;
    }
  },

  watch: {
    /**
     * for A11y - focuses on checkout button when cart opens,
     * and returns focus when cart closes
     */
    isOpen(newIsOpen) {
      this.managePageScroll(newIsOpen, this.$refs.scrollTarget);

      try {
        if (newIsOpen === true) {
          previouslyFocusedElement = document.activeElement;

          this.$refs.checkout.focus();
        } else if (previouslyFocusedElement !== undefined) {
          previouslyFocusedElement.focus();

          previouslyFocusedElement = undefined;
        }
      } catch (err) {
        console.error(err);
      }
    }
  },

  mounted() {
    this.fetchState();

    window.location.hash === '#open_cart' && (this.isOpen = true);

    this.$globalEventBus.addListener('flyoutCart.toggle', () => {
      this.isOpen = !this.isOpen;
      this.fetchState();
    });

    this.$globalEventBus.addListener('flyoutCart.addItem', () => {
      this.isOpen = true;
      this.fetchState();
    });
  },

  beforeDestroy() {
    clearAllBodyScrollLocks();
  },

  methods: {
    ...mapActions({
      fetchState: `${flyoutCart}/${actions.fetchState}`,
      updateItem: `${flyoutCart}/${actions.updateItem}`,
      removeItem: `${flyoutCart}/${actions.removeItem}`
    }),

    onCheckoutButtonClick() {
      if (this.isEmpty) {
        this.isOpen = false;
      } else {
        if (this.requireCustomerForCheckout && !this.customerId) {
          this.redirectToLogin();
        } else {
          this.checkout();
        }
      }
    },

    checkout() {
      window.location = this.checkoutURL;
    },

    redirectToLogin() {
      window.localStorage.setItem('redirect_url', this.checkoutURL);
      window.location = this.loginUrl;
    },

    managePageScroll(isOpen, targetElement) {
      const maxScreenWidth = 1024; // breakpoint large: 1024px;
      if (maxScreenWidth < window.innerWidth) return;
      if (typeof isOpen !== 'boolean' || !targetElement) return;

      isOpen
        ? disableBodyScroll(targetElement)
        : enableBodyScroll(targetElement);
    }
  }
};
</script>
